import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import KeyIcon from "@mui/icons-material/Key";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import DashboardIcon from "@mui/icons-material/Dashboard";
import clsx from "clsx";
import UserGuideTooltip from "components/shared-ui/user-guide-tooltip/user-guide-tooltip";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { NavLink, NavLinkProps } from "react-router-dom";
import { Action } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { accountService } from "../../api/services/account-service";
import {
  ACCESS_CREDENTIALS_ROUTE,
  BUCKETS_ROUTE,
  DASHBOARD_ROUTE,
  LOGOUT_ROUTE,
  SETTINGS_ROUTE,
  SHARD_REPOSITORIES_ROUTE,
  SHARD_CLUSTERS_ROUTE,
  getAbsoluteRoute,
  GROUPS_ROUTE,
  ROLES_ROUTE,
  USERS_ROUTE,
  POLICIES_ROUTE,
  STORAGE_PROVIDERS_ROUTE,
  ARCHIVE_ROUTE,
  APP_LOGS_ROUTE,
} from "../../constants/routes";
import { accountThunkActions } from "../../redux/actions/account";
import { commonActions } from "../../redux/actions/common";
import { tooltipUserGuideActions } from "../../redux/actions/tooltip-user-guide";
import { IAppState } from "../../redux/reducers";
import Button from "../shared-ui/button";
import LeftPanel from "../shared-ui/left-panel/left-panel";
import styles from "./left-nav.module.scss";
import {
  FolderZip,
  PeopleAlt,
  Person,
  Policy,
  ReceiptLong,
} from "@mui/icons-material";
import { HelpDialog } from "components/help/help-dialog";

interface ILeftNavV2SectionItem extends Pick<NavLinkProps, "to" | "className"> {
  dataTestId: string;
  forceActive?: boolean;
  displayName: React.ReactNode;
  icon: React.ReactNode;
}

interface ILeftNavV2SectionProps {
  sectionTitle: string;
  tooltipOpen: boolean;
  hiddenOnSmallScreen?: boolean;
  items: ILeftNavV2SectionItem[];
}

function LeftNavSection(props: ILeftNavV2SectionProps) {
  return (
    <div
      className={
        props.hiddenOnSmallScreen ? styles["hidden-on-small-screen"] : ""
      }
    >
      <div className={styles["nav-link-separator"]}>{props.sectionTitle}</div>
      {props.items.map((item) => (
        <NavLink
          key={item.dataTestId}
          data-testid={item.dataTestId}
          to={item.to}
          className={(nav) =>
            clsx(
              styles["nav-link"],
              item.className,
              ((nav.isActive && !props.tooltipOpen) || item.forceActive) &&
                styles["nav-link--active"],
              ((nav.isActive && !props.tooltipOpen) || item.forceActive) &&
                styles["--hover"],
            )
          }
        >
          {item.icon}
          {item.displayName}
        </NavLink>
      ))}
    </div>
  );
}

interface ILeftNavV2Props
  extends
    ReturnType<typeof mapStateToProps>,
    ReturnType<typeof mapDispatchToProps> {}

function LeftNav(props: ILeftNavV2Props) {
  const iamSection: ILeftNavV2SectionProps = {
    sectionTitle: "Users & groups",
    tooltipOpen: props.tooltipOpen,
    items: [
      {
        dataTestId: "navbar-link-groups",
        icon: <PeopleAlt className={styles["nav-link__icon"]} />,
        to: getAbsoluteRoute(GROUPS_ROUTE),
        className: styles["nav-link--groups"],
        displayName: <span>Groups</span>,
      },
      // {
      //   dataTestId: "navbar-link-roles",
      //   icon: <AssignmentInd className={styles["nav-link__icon"]} />,
      //   to: getAbsoluteRoute(ROLES_ROUTE),
      //   className: styles["nav-link--roles"],
      //   displayName: <span>Roles</span>,
      // },
      {
        dataTestId: "navbar-link-users",
        icon: <Person className={styles["nav-link__icon"]} />,
        to: getAbsoluteRoute(USERS_ROUTE),
        className: styles["nav-link--users"],
        displayName: <span>Users</span>,
      },
    ],
  };

  const monitoringSection: ILeftNavV2SectionProps = {
    sectionTitle: "Monitoring",
    tooltipOpen: props.tooltipOpen,
    items: [
      {
        dataTestId: "navbar-link-dashboard",
        icon: <DashboardIcon className={styles["nav-link__icon"]} />,
        to: getAbsoluteRoute(DASHBOARD_ROUTE),
        forceActive: false,
        className: styles["nav-link--dashboard"],
        displayName: (
          <>
            <span>Dashboard</span>
            {/* <UserGuideTooltip entityName="firstStep" /> */}
          </>
        ),
      },
    ],
  };

  const dataAccessSection: ILeftNavV2SectionProps = {
    sectionTitle: "Data Access",
    tooltipOpen: props.tooltipOpen,
    items: [
      {
        dataTestId: "navbar-link-buckets",
        icon: <Inventory2Icon className={styles["nav-link__icon"]} />,
        to: getAbsoluteRoute(BUCKETS_ROUTE),
        className: styles["nav-link--buckets"],
        forceActive:
          props.tooltipOpen && props.tooltipUserGuideEntity === "bucket",
        displayName: (
          <>
            <span>Buckets</span>
            <UserGuideTooltip entityName="bucket" />
          </>
        ),
      },
      {
        dataTestId: "navbar-link-policies",
        icon: <Policy className={styles["nav-link__icon"]} />,
        to: getAbsoluteRoute(POLICIES_ROUTE),
        className: styles["nav-link--policies"],
        displayName: <span>Policies</span>,
      },
      {
        dataTestId: "navbar-link-application-credentials",
        icon: <KeyIcon className={styles["nav-link__icon"]} />,
        to: getAbsoluteRoute(ACCESS_CREDENTIALS_ROUTE),
        forceActive:
          props.tooltipOpen &&
          props.tooltipUserGuideEntity === "accessCredentials",
        className: styles["nav-link--access-credentials"],
        displayName: (
          <>
            <span>Access Credentials</span>
            <UserGuideTooltip entityName="accessCredentials" />
          </>
        ),
      },
    ],
  };

  const shredAndSpreadSection: ILeftNavV2SectionProps = {
    sectionTitle: "Shard & Spread Configuration",
    tooltipOpen: props.tooltipOpen,
    items: [
      {
        dataTestId: "navbar-link-nodes",
        icon: <StorageOutlinedIcon className={styles["nav-link__icon"]} />,
        to: getAbsoluteRoute(SHARD_REPOSITORIES_ROUTE),
        className: styles["nav-link--nodes"],
        forceActive:
          props.tooltipOpen &&
          props.tooltipUserGuideEntity === "shardRepository",
        displayName: (
          <>
            <span>Shard Repositories</span>
            <UserGuideTooltip entityName="shardRepository" />
          </>
        ),
      },
      // {
      //   dataTestId: "navbar-link-pools",
      //   to: getAbsoluteRoute(SHARD_CLUSTERS_ROUTE),
      //   className: styles["nav-link--pools"],
      //   icon: <CloudQueueIcon className={styles["nav-link__icon"]} />,
      //   forceActive:
      //     props.tooltipOpen && props.tooltipUserGuideEntity === "shardCluster",
      //   displayName: (
      //     <>
      //       <span>Shard Clusters</span>
      //       <UserGuideTooltip entityName="shardCluster" />
      //     </>
      //   ),
      // },
      {
        dataTestId: "navbar-link-providers",
        to: getAbsoluteRoute(STORAGE_PROVIDERS_ROUTE),
        className: styles["nav-link--providers"],
        icon: <CloudQueueIcon className={styles["nav-link__icon"]} />,
        forceActive:
          props.tooltipOpen &&
          props.tooltipUserGuideEntity === "storageProvider",
        displayName: (
          <>
            <span className={styles["provider-name"]}>
              Shard Repository Providers
            </span>
            <UserGuideTooltip entityName="storageProvider" />
          </>
        ),
      },
    ],
  };

  const accountSection: ILeftNavV2SectionProps = {
    sectionTitle: "Your Account",
    hiddenOnSmallScreen: true,
    tooltipOpen: props.tooltipOpen,
    items: [
      {
        dataTestId: "navbar-link-profile",
        to: getAbsoluteRoute(SETTINGS_ROUTE),
        className: (nav) =>
          nav.isActive
            ? styles["nav-link--active-non"]
            : styles["hidden-on-small-screen"],
        icon: <AccountCircleIcon className={styles["nav-link__icon"]} />,
        displayName: (
          <span className={styles["account-name"]}>{props.userName}</span>
        ),
      },
    ],
  };

  const operationsSection: ILeftNavV2SectionProps = {
    sectionTitle: "Operations",
    hiddenOnSmallScreen: true,
    tooltipOpen: props.tooltipOpen,
    items: [
      {
        dataTestId: "navbar-link--app-logs",
        icon: <ReceiptLong className={styles["nav-link__icon"]} />,
        to: getAbsoluteRoute(APP_LOGS_ROUTE),
        className: styles["nav-link--archive"],
        displayName: (
          <>
            <span>Logs</span>
          </>
        ),
      },
      {
        dataTestId: "navbar-link--archive",
        icon: <FolderZip className={styles["nav-link__icon"]} />,
        to: getAbsoluteRoute(ARCHIVE_ROUTE),
        className: styles["nav-link--archive"],
        displayName: (
          <>
            <span>Archived Configs</span>
            <UserGuideTooltip entityName="firstStep" />
          </>
        ),
      },
    ],
  };

  const [helpOpen, setHelpOpen] = useState<boolean>(false);

  useEffect(() => {
    let tooltipUserGuideState = accountService.getTooltipUserGuideState();

    if (tooltipUserGuideState.showTooltip) {
      props.tooltipUserGuideOpen();
    } else {
      props.tooltipUserGuideClose();
    }
    // eslint-disable-next-line
  }, []);

  return (
    <LeftPanel rootClass={styles.root} testId="left-nav">
      <div className={styles.section}>
        <LeftNavSection
          sectionTitle={monitoringSection.sectionTitle}
          items={monitoringSection.items}
          tooltipOpen={monitoringSection.tooltipOpen}
        />
        <LeftNavSection
          sectionTitle={dataAccessSection.sectionTitle}
          items={dataAccessSection.items}
          tooltipOpen={dataAccessSection.tooltipOpen}
        />
        <LeftNavSection
          sectionTitle={shredAndSpreadSection.sectionTitle}
          items={shredAndSpreadSection.items}
          tooltipOpen={shredAndSpreadSection.tooltipOpen}
        />
        <LeftNavSection
          sectionTitle={iamSection.sectionTitle}
          items={iamSection.items}
          tooltipOpen={iamSection.tooltipOpen}
          hiddenOnSmallScreen={iamSection.hiddenOnSmallScreen}
        />
        <LeftNavSection
          sectionTitle={operationsSection.sectionTitle}
          items={operationsSection.items}
          tooltipOpen={operationsSection.tooltipOpen}
          hiddenOnSmallScreen={operationsSection.hiddenOnSmallScreen}
        />
      </div>

      <div className={styles.section}>
        <LeftNavSection
          sectionTitle={accountSection.sectionTitle}
          items={accountSection.items}
          tooltipOpen={accountSection.tooltipOpen}
          hiddenOnSmallScreen={accountSection.hiddenOnSmallScreen}
        />
        <Button
          colors="link"
          testId="navbar-tooltip-user-guide-toggle"
          className={clsx(
            styles["nav-link"],
            styles["nav-link--tooltip-user-guide-toggle"],
            styles["hidden-on-small-screen"],
          )}
          onClick={() => setHelpOpen(true)}
        >
          <QuestionMarkIcon className={styles["nav-link__icon"]} />
          About
        </Button>
        <a
          data-testid="navbar-link-logout"
          onClick={() => props.startLoading()}
          className={clsx(
            styles["nav-link"],
            styles["nav-link--logout"],
            styles["hidden-on-small-screen"],
          )}
          href={LOGOUT_ROUTE}
        >
          <PowerSettingsNewIcon className={styles["nav-link__icon"]} />
          Logout
        </a>
      </div>
      <HelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
    </LeftPanel>
  );
}

const mapStateToProps = (state: IAppState) => ({
  userName: state.users.currentUser.name,
  accountType: state.users.currentUser.accountType,
  organization: state.users.currentUser.organization,
  isAdmin: state.users.currentUser.isAdmin,
  clusterStatus: state.users.currentUser.clusterStatus,
  tooltipOpen: state.tooltipUserGuide.open,
  tooltipUserGuideEntity: state.tooltipUserGuide.currentEntity,
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<IAppState, void, Action>,
) => {
  return {
    logout: () => {
      dispatch(accountThunkActions.logout());
    },
    startLoading: () => dispatch(commonActions.startLoading()),

    tooltipUserGuideOpen: () => {
      dispatch(tooltipUserGuideActions.enableStepCounter());
      dispatch(commonActions.setLeftPanelOpen(true));
      dispatch(tooltipUserGuideActions.open());
    },
    tooltipUserGuideClose: () => {
      dispatch(tooltipUserGuideActions.close());
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(LeftNav);
